import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  ArrowLeft, Copy, Download, AlertCircle, Loader2,
  Package, Clock, DollarSign, Bot, GitCompare,
  CheckCircle2, XCircle, AlertTriangle, BarChart3,
  FileText, Mail, Activity, X, ShieldAlert, TrendingUp, Archive, Info,
  Send,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { RiskBadge, RecommendationBadge, getAlertTier } from '../components/Badges';
import { BarChart, Bar, Cell, PieChart, Pie, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts';
import ActionPlan from '../components/ActionPlan';
import SeriesProgressionCard from '../components/SeriesProgressionCard';
import RelatedAudits from '../components/RelatedAudits';
import AgentInsights from '../components/AgentInsights';
import { ThreadTimeline, ProgressSummaryPanel } from '../components/ThreadTracker';
import ComposeModal from '../components/ComposeModal';
import { useAuth } from '../contexts/AuthContext';
import { extractThreadParticipants, parseAddress } from '../lib/emailAddresses';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────────────────────
// SECTION B — FULL INVENTORY AUDIT TABLE
// Single source of truth for parsing & styling the pipe-separated table
// embedded in the audit email draft. Both the React renderer
// (EmailDraftRenderer) and the rich-HTML clipboard builder
// (buildRichEmailHtml) reuse these helpers — no duplicates.
// ─────────────────────────────────────────────────────────────────────────────

// Flag color palette — used by reject cards & status pills
const FLAG_COLORS = {
  PORTFOLIO_AGEING_BLOCK: { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', label: 'Reorder Blocked · Portfolio Ageing' },
  STAGNANT_INVENTORY: { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', label: 'Zero Sales / Stagnant' },
  HIGH_WOS:           { bg: '#fef2f2', border: '#fca5a5', text: '#991b1b', label: 'Excess Stock (High WOS)' },
  PRICE_INCREASE:     { bg: '#fffbeb', border: '#fcd34d', text: '#92400e', label: 'PI Price Increase' },
  MEDIUM_WOS:         { bg: '#fffbeb', border: '#fcd34d', text: '#92400e', label: 'Medium WOS' },
  AGEING_INVENTORY:   { bg: '#f5f3ff', border: '#c4b5fd', text: '#5b21b6', label: 'Ageing >60 Days' },
};

// Strip leading/trailing markdown noise so renderer + clipboard agree on input
import { stripAiPreamble as cleanReportText } from '../lib/stripAiPreamble';

// Pipe-table line parsers
const splitPipeCells = (s) => {
  let parts = String(s).split('|').map(x => x.trim());
  if (parts.length && parts[0] === '') parts = parts.slice(1);
  if (parts.length && parts[parts.length - 1] === '') parts = parts.slice(0, -1);
  return parts;
};
const isPipeHeaderLine = (s) => {
  if (!s.includes('|')) return false;
  const parts = splitPipeCells(s);
  return parts.length >= 2 && parts.every(p => p.length > 0 && /[A-Za-z]/.test(p));
};
const isPipeSepLine = (s) => s.includes('-') && /^[\s|\-=:]+$/.test(s);
const isPipeDataLine = (s) => {
  if (!s.includes('|')) return false;
  return splitPipeCells(s).length >= 2;
};

// [FLAG] cell helpers
const cellHasFlag = (val) => /\[FLAG\]/i.test(val || '');
const stripFlag   = (val) => (val || '').replace(/\s*\[FLAG\]\s*/gi, '').trim();

// Approval / success summary line detection (rendered in green)
const isApprovalLine = (line) => {
  if (!line || !line.trim()) return false;
  if (/\b(reject|review|issue|flag|exceed|stagnant)\b/i.test(line)) return false;
  return /\b(approved|auto[-\s]?approved|new product launch(es)?)\b/i.test(line);
};

// Map a header label to a column "kind" for styling
const getColKind = (header) => {
  const lh = (header || '').toLowerCase();
  if (/status|decision|recommend/.test(lh))                      return 'status';
  if (/wos|stock\s*weeks?/.test(lh))                             return 'wos';
  if (/^\s*stock\s*$|qty.*stock|in.?stock|on.?hand/.test(lh))    return 'stock';
  if (/ageing|aging|>\s*60/.test(lh))                            return 'ageing';
  if (/price/.test(lh))                                          return 'price';
  if (/item|sku|model|product/.test(lh))                         return 'name';
  return 'text';
};

// Parse the cleaned report into typed segments: { type:'text', value } | { type:'table', header, rows }
const parseEmailDraftSegments = (cleaned) => {
  if (!cleaned) return [];
  const lines = cleaned.split('\n');
  const out = [];
  let buffer = [];
  let i = 0;

  const flushText = () => {
    if (buffer.length) { out.push({ type: 'text', value: buffer.join('\n') }); buffer = []; }
  };

  while (i < lines.length) {
    const line = lines[i];
    // Strict markdown table: header + separator + data rows
    if (isPipeHeaderLine(line) && i + 1 < lines.length && isPipeSepLine(lines[i + 1])) {
      flushText();
      const header = splitPipeCells(line);
      const rows = [];
      let j = i + 2;
      while (j < lines.length && isPipeDataLine(lines[j])) {
        const cells = splitPipeCells(lines[j]);
        while (cells.length < header.length) cells.push('');
        rows.push(cells.slice(0, header.length));
        j++;
      }
      out.push({ type: 'table', header, rows });
      i = j;
      continue;
    }
    // Implicit pipe table: header + data rows (no separator line). Triggered
    // when the AI omits the markdown rule below the header — e.g.
    //   Item Name | Stock | WOS | Ageing | Price | Status
    //   SAMSUNG GALAXY A16 ... | 610 | No sales (fresh stock) | 0% | n/a | ALERT
    // Without this branch the parser fell back to plain text and rendered
    // pipe-separated lines as a wall of unstyled text.
    if (isPipeHeaderLine(line) && i + 1 < lines.length && isPipeDataLine(lines[i + 1])) {
      const header = splitPipeCells(line);
      const nextCells = splitPipeCells(lines[i + 1]);
      // Require column-count alignment between header and the first data row
      // — guards against accidental triggers on prose with stray pipes.
      const aligned = header.length >= 3 && Math.abs(nextCells.length - header.length) <= 1;
      if (aligned) {
        flushText();
        const rows = [];
        let j = i + 1;
        while (j < lines.length && isPipeDataLine(lines[j])) {
          const cells = splitPipeCells(lines[j]);
          while (cells.length < header.length) cells.push('');
          rows.push(cells.slice(0, header.length));
          j++;
        }
        // Need at least 1 data row to commit — otherwise it was probably a
        // standalone pipe-list, not a table.
        if (rows.length >= 1) {
          out.push({ type: 'table', header, rows });
          i = j;
          continue;
        }
      }
    }
    buffer.push(line);
    i++;
  }
  flushText();
  return out;
};

// ─── EmailDraftRenderer ──────────────────────────────────────────────────────
function EmailDraftRenderer({ report, lineItems, subject, country, portfolioAgeing }) {
  const items = lineItems || [];
  const highAlertItems = items.filter(i => getAlertTier(i) === 'HIGH_ALERT');
  const alertItems     = items.filter(i => getAlertTier(i) === 'ALERT');
  // Items that need an immediate-attention card (REJECTed in the rules engine)
  const breakdownItems = items.filter(i => i.recommendation === 'REJECT');
  const totalAlerts    = highAlertItems.length + alertItems.length;
  const totalSKUs      = items.length;
  const pa             = portfolioAgeing || {};
  const portfolioBlocked = !!pa.blocked;

  // Use shared cleanup + segment parser (single source of truth)
  const cleaned  = React.useMemo(() => cleanReportText(report), [report]);
  const segments = React.useMemo(() => parseEmailDraftSegments(cleaned), [cleaned]);

  return (
    <div data-testid="audit-email-draft" style={{ display: 'flex', flexDirection: 'column', gap: 16, maxHeight: 620, overflowY: 'auto' }}>

      {/* Subject header */}
      {subject && (
        <div style={{
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12,
          padding: '12px 18px', display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          <p style={{ fontSize: 13, color: '#0f172a', margin: 0 }}>
            <span style={{ fontWeight: 700, color: '#475569', marginRight: 6 }}>Subject:</span>
            <span data-testid="email-draft-subject">{subject}</span>
          </p>
        </div>
      )}

      {/* Portfolio Ageing banner — top-of-email gate */}
      {portfolioBlocked && (
        <div data-testid="portfolio-ageing-block" style={{
          background: '#fef2f2', border: '2px solid #dc2626', borderRadius: 12,
          padding: '14px 18px', display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <ShieldAlert style={{ width: 20, height: 20, color: '#dc2626', flexShrink: 0, marginTop: 1 }} />
          <div>
            <p style={{ fontWeight: 800, fontSize: 14, color: '#991b1b', margin: 0, letterSpacing: '0.02em' }}>
              REORDER BLOCKED — Portfolio Ageing {pa.pct}%
              {pa.week ? ` (${pa.week})` : ''} (threshold {pa.threshold_pct || 10}%)
            </p>
            <p style={{ fontSize: 12, color: '#b91c1c', margin: '3px 0 0' }}>
              {pa.basis === 'value'
                ? <>${Number(pa.total_aged || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} aged / ${Number(pa.total_stock || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} closing stock value.</>
                : <>{Number(pa.total_aged || 0).toLocaleString()} of {Number(pa.total_stock || 0).toLocaleString()} units across the inventory are aged &gt;60 days.</>
              } Liquidate aged stock before placing this order.
            </p>
          </div>
        </div>
      )}
      {!portfolioBlocked && pa.has_data && (
        <div style={{
          background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12,
          padding: '8px 14px', fontSize: 12, color: '#166534',
        }}>
          <span style={{ fontWeight: 600, marginRight: 6 }}>Portfolio ageing OK:</span>
          {pa.pct}% of {pa.basis === 'value' ? 'closing stock value' : 'inventory'} aged &gt;60 days
          {pa.week ? ` (${pa.week})` : ''} (threshold {pa.threshold_pct || 10}%)
        </div>
      )}

      {/* Summary banner — tier-based */}
      {totalAlerts > 0 ? (
        <div style={{ background: highAlertItems.length > 0 ? '#fef2f2' : '#fff7ed',
                      border: `1.5px solid ${highAlertItems.length > 0 ? '#fca5a5' : '#fdba74'}`,
                      borderRadius: 12, padding: '14px 18px',
                      display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <ShieldAlert style={{ width: 18, height: 18,
                                color: highAlertItems.length > 0 ? '#dc2626' : '#ea580c',
                                flexShrink: 0, marginTop: 1 }} />
          <div>
            <p style={{ fontWeight: 700, fontSize: 14,
                        color: highAlertItems.length > 0 ? '#991b1b' : '#9a3412', margin: 0 }}>
              {highAlertItems.length > 0
                ? `${highAlertItems.length} HIGH ALERT${highAlertItems.length !== 1 ? ' items' : ' item'} — Immediate Action Required`
                : `${alertItems.length} ALERT${alertItems.length !== 1 ? ' items' : ' item'} — Review Required`}
            </p>
            <p style={{ fontSize: 12,
                        color: highAlertItems.length > 0 ? '#b91c1c' : '#c2410c',
                        margin: '3px 0 0' }}>
              {totalSKUs} total SKUs audited.
              {highAlertItems.length > 0 && alertItems.length > 0
                ? ` ${alertItems.length} additional ALERT item(s) for review.`
                : ''}
            </p>
          </div>
        </div>
      ) : (
        <div style={{ background: '#f0fdf4', border: '1.5px solid #86efac', borderRadius: 12, padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10 }}>
          <CheckCircle2 style={{ width: 18, height: 18, color: '#16a34a', flexShrink: 0 }} />
          <p style={{ fontWeight: 600, fontSize: 14, color: '#15803d', margin: 0 }}>
            All {totalSKUs} SKUs within thresholds — reorder approved.
          </p>
        </div>
      )}

      {/* Email body — text mixed with parsed pipe-tables rendered as Excel-like tables
          using the same look-and-feel as the Executive Summary section. */}
      {cleaned && (
        <div style={{
          fontFamily: "'Outfit', 'Helvetica Neue', Arial, sans-serif",
          fontSize: 14, lineHeight: 1.75, color: '#1e293b',
          background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0',
          padding: '18px 22px',
        }}>
          {segments.map((seg, idx) => {
            if (seg.type === 'text') {
              const lines = seg.value.split('\n');
              return (
                <div key={idx} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {lines.map((ln, li) => (
                    <React.Fragment key={li}>
                      {isApprovalLine(ln) ? (
                        <span style={{ color: '#15803d', fontWeight: 600 }}>{ln}</span>
                      ) : ln}
                      {li < lines.length - 1 ? '\n' : ''}
                    </React.Fragment>
                  ))}
                </div>
              );
            }

            // ── Pipe table → render in the SAME style as the Executive Summary table.
            // Column kinds come from the shared getColKind() helper (single source of truth).
            const colKinds = seg.header.map(getColKind);

            return (
              <div key={idx} className="overflow-x-auto rounded-xl border border-border my-4">
                <table className="audit-table w-full" data-testid="email-draft-table">
                  <thead>
                    <tr className="bg-secondary/60 border-b border-border">
                      {seg.header.map((h, hi) => (
                        <th key={hi} className="text-left px-4 py-3">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {seg.rows.map((row, ri) => (
                      <tr key={ri} className="border-b border-border/50 last:border-0 hover:bg-accent/40 transition-colors">
                        {row.map((rawCell, ci) => {
                          const flagged = cellHasFlag(rawCell);
                          const cell = stripFlag(rawCell);
                          const kind = colKinds[ci] || 'text';

                          // STATUS column → RecommendationBadge using tier directly
                          // Backend now emits HIGH ALERT / ALERT / ALLOW; legacy reports
                          // may still contain REJECT/REVIEW so we map both.
                          if (kind === 'status') {
                            const upper = (cell || '').toUpperCase();
                            let resolvedTier = null;
                            if (upper.includes('HIGH ALERT'))                          resolvedTier = 'HIGH_ALERT';
                            else if (upper.includes('ALERT'))                          resolvedTier = 'ALERT';
                            else if (upper.includes('ALLOW') || upper.includes('APPROVE')) resolvedTier = 'ALLOW';
                            else if (upper.includes('REJECT'))                         resolvedTier = 'HIGH_ALERT';
                            else if (upper.includes('REVIEW'))                         resolvedTier = 'ALERT';
                            return (
                              <td key={ci} className="px-4 py-3">
                                {resolvedTier ? <RecommendationBadge tier={resolvedTier} /> : (cell || '—')}
                              </td>
                            );
                          }

                          // WOS column → RiskBadge using grade if recognisable
                          if (kind === 'wos') {
                            const upper = (cell || '').toUpperCase();
                            const grade = flagged || /STAGNANT/.test(upper) ? 'STAGNANT'
                                        : /HIGH|VERY/.test(upper)           ? 'FLAG'
                                        : /MED/.test(upper)                 ? 'CHECK'
                                        : /OK|LOW|NEW/.test(upper)          ? 'OK'
                                        : flagged                            ? 'FLAG'
                                        : 'OK';
                            return (
                              <td key={ci} className="px-4 py-3">
                                <RiskBadge grade={grade} label={cell || '—'} />
                              </td>
                            );
                          }

                          // AGEING column → red bold + 🚩 when flagged (matches Executive Summary)
                          if (kind === 'ageing') {
                            return (
                              <td key={ci} className="px-4 py-3 tabular-nums text-sm">
                                <span className={flagged ? 'text-red-600 font-semibold' : ''}>
                                  {cell || '—'}{flagged ? ' 🚩' : ''}
                                </span>
                              </td>
                            );
                          }

                          // PRICE column → red bold + 🚩 when flagged
                          if (kind === 'price') {
                            const isOk = /^ok$/i.test(cell);
                            const isNa = /^n\/?a$/i.test(cell);
                            return (
                              <td key={ci} className="px-4 py-3 tabular-nums text-sm">
                                {isOk ? (
                                  <span className="text-emerald-700">OK</span>
                                ) : isNa ? (
                                  <span className="text-muted-foreground text-xs">no price data</span>
                                ) : (
                                  <span className={flagged ? 'text-red-600 font-semibold' : ''}>
                                    {cell || '—'}{flagged ? ' 🚩' : ''}
                                  </span>
                                )}
                              </td>
                            );
                          }

                          // NAME column → mono, truncated, hover for full name
                          if (kind === 'name') {
                            return (
                              <td key={ci} className="px-4 py-3">
                                <span
                                  className="model-cell truncate-cell font-mono text-xs font-medium block max-w-[200px] sm:max-w-[260px]"
                                  title={cell}
                                >{cell || '—'}</span>
                              </td>
                            );
                          }

                          // STOCK column → tabular numeric, right-aligned
                          if (kind === 'stock') {
                            return (
                              <td key={ci} className="px-4 py-3 tabular-nums text-sm font-medium text-slate-700">
                                {cell || '—'}
                              </td>
                            );
                          }

                          // Generic cell
                          return (
                            <td key={ci} className="px-4 py-3 text-sm">
                              <span className={flagged ? 'text-red-600 font-semibold' : ''}>
                                {cell || '—'}{flagged ? ' 🚩' : ''}
                              </span>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}
        </div>
      )}

      {/* Alert item cards */}
      {breakdownItems.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8', margin: 0 }}>
            Alert Items — Detailed Breakdown
          </p>
          {breakdownItems.map((item, idx) => {
            const tier = getAlertTier(item);  // 'HIGH_ALERT' | 'ALERT'
            const tierLabel = tier === 'HIGH_ALERT' ? 'HIGH ALERT' : 'ALERT';
            const primaryFlag = (item.flags || [])[0] || 'HIGH_WOS';
            const fc = FLAG_COLORS[primaryFlag] || FLAG_COLORS['HIGH_WOS'];
            return (
              <div key={idx} style={{ border: `2px solid ${fc.border}`, borderRadius: 12, background: '#fff', overflow: 'hidden' }}>
                {/* Card header */}
                <div style={{ background: fc.bg, padding: '10px 14px', borderBottom: `1px solid ${fc.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                    <ShieldAlert style={{ width: 15, height: 15, color: fc.text, flexShrink: 0 }} />
                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, fontWeight: 600, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.model}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, fontWeight: 800, color: fc.text, background: '#fff', border: `1px solid ${fc.border}`, borderRadius: 20, padding: '2px 8px', whiteSpace: 'nowrap', letterSpacing: '0.04em' }}>
                      {tierLabel}
                    </span>
                    <span style={{ fontSize: 10, fontWeight: 600, color: fc.text, background: '#fff', border: `1px solid ${fc.border}`, borderRadius: 20, padding: '2px 8px', whiteSpace: 'nowrap' }}>
                      {fc.label}
                    </span>
                  </div>
                </div>
                {/* Metrics */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, padding: '10px 14px' }}>
                  {[
                    ['WOS', item.wos_label, fc.text],
                    ['Stock', (item.current_stock || 0).toLocaleString(), '#334155'],
                    ['Sales (4w)', (item.sales_last_4w || 0).toLocaleString(), '#334155'],
                    item.price_variance != null
                      ? ['Price Δ', `${item.price_variance >= 0 ? '+' : ''}${item.price_variance?.toFixed(2)}`, '#b45309']
                      : ['Ageing', `${item.ageing_pct || 0}%`, '#6d28d9'],
                  ].map(([label, value, color]) => (
                    <div key={label}>
                      <p style={{ fontSize: 10, color: '#94a3b8', margin: '0 0 2px' }}>{label}</p>
                      <p style={{ fontSize: 13, fontWeight: 700, color, margin: 0, fontVariantNumeric: 'tabular-nums' }}>{value}</p>
                    </div>
                  ))}
                </div>
                {/* Reason */}
                {item.recommendation_reason && (
                  <div style={{ padding: '0 14px 10px', borderTop: `1px solid ${fc.border}` }}>
                    <p style={{ fontSize: 11, color: '#64748b', margin: '8px 0 0', fontStyle: 'italic' }}>
                      {item.recommendation_reason}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
const STAT_CARDS = [
  {
    key: 'ALL', label: 'Total SKUs', valueKey: 'total_skus',
    icon: Package,
    iconBg:    'bg-primary/10',
    iconColor: 'text-primary',
    numColor:  'text-foreground',
    ring:      'ring-primary',
    activeBg:  'bg-primary/5 border-primary',
  },
  {
    key: 'ALLOW', label: 'ALLOW', valueKey: 'allow',
    icon: CheckCircle2,
    iconBg:    'bg-emerald-100',
    iconColor: 'text-emerald-600',
    numColor:  'text-emerald-700',
    ring:      'ring-emerald-400',
    activeBg:  'bg-emerald-50 border-emerald-300',
    bar:       { color: '#059669', label: '% of total' },
  },
  {
    key: 'ALERT', label: 'ALERT', valueKey: 'alert',
    icon: AlertTriangle,
    iconBg:    'bg-orange-100',
    iconColor: 'text-orange-600',
    numColor:  'text-orange-700',
    ring:      'ring-orange-400',
    activeBg:  'bg-orange-50 border-orange-300',
    bar:       { color: '#ea580c', label: '% of total' },
  },
  {
    key: 'HIGH_ALERT', label: 'HIGH ALERT', valueKey: 'high_alert',
    icon: ShieldAlert,
    iconBg:    'bg-red-100',
    iconColor: 'text-red-500',
    numColor:  'text-red-700',
    ring:      'ring-red-400',
    activeBg:  'bg-red-50 border-red-300',
    bar:       { color: '#dc2626', label: '% of total' },
  },
];

// ─ Custom tab bar ─
const TABS = [
  { key: 'executive', label: 'Executive Summary', icon: BarChart3 },
  { key: 'findings',  label: 'Findings',          icon: Activity   },
  { key: 'draft',     label: 'Email Draft',       icon: Mail       },
  { key: 'thread',    label: 'Mail Thread',       icon: Mail       },
  { key: 'progress',  label: 'AI Progress',       icon: Activity   },
  { key: 'log',       label: 'Agent Log',         icon: Bot        },
];

function ClassicTabs({ active, setActive, tabs, agentCount, discCount }) {
  return (
    <div className="border-b border-border mb-0">
      <div className="flex gap-0 overflow-x-auto">
        {tabs.map(({ key, label, icon: Icon }) => {
          const isActive = active === key;
          const badge =
            key === 'log' && (agentCount || discCount)
              ? (discCount || agentCount || 0)
              : null;
          return (
            <button
              key={key}
              data-testid={`tab-${key}`}
              onClick={() => setActive(key)}
              className={`relative flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap
                transition-colors duration-150 focus:outline-none
                ${isActive ? 'text-primary' : 'text-muted-foreground hover:text-foreground'}`}
            >
              <Icon className="w-4 h-4" />
              {label}
              {badge != null && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold
                  ${isActive ? 'bg-primary/15 text-primary' : 'bg-border text-muted-foreground'}`}>
                  {badge}
                </span>
              )}
              {isActive && (
                <motion.div
                  layoutId="tab-underline"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full"
                  transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// \u2500 Smart warning classifier \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
function getSmartWarnings(warnings, auditArg) {
  const out = [];
  const safeWarnings = Array.isArray(warnings) ? warnings : [];
  const audit = (auditArg && typeof auditArg === 'object') ? auditArg : {};
  const joined = safeWarnings.join(' ').toLowerCase();
  const agentActions = (audit.agent_actions || []).join(' ').toLowerCase();
  const combined = joined + ' ' + agentActions;

  // ===== AUTO-RESOLUTION NOTIFICATIONS =====
  
  // Success: Previous audit auto-resolved
  if (audit.agent_insights?.auto_resolved_previous) {
    const ar = audit.agent_insights.auto_resolved_previous;
    out.push({
      type: 'success',
      icon: '🎉',
      text: `${ar.team_member} responded ${ar.on_time ? 'on time' : ''}! Previous audit auto-resolved: ${Math.round(ar.resolution_rate * 100)}% of issues fixed (${ar.response_time_days}d response time).`,
    });
  }
  
  // Manual Decision Needed: Low resolution rate or late response
  if (audit.agent_insights?.manual_resolution_needed) {
    const mr = audit.agent_insights.manual_resolution_needed;
    out.push({
      type: 'warn',
      icon: '⚠️',
      text: `${audit.team_member?.name || 'Team member'} responded but ${mr.reason}. Previous audit still awaiting your decision - check Action Tracker.`,
    });
  }

  // ===== NEW: ACCOUNTABILITY ALERTS =====
  
  // Repeat Auditor Alert - pending actions from same team member
  if (audit.related_audits && audit.related_audits.length > 0) {
    // Check if any related audits have pending/awaiting action status
    const hasPendingActions = audit.related_audits.some(r => {
      // Note: related_audits may not have full action_plan, check main audit
      return r.relationship === 'previous';
    });
    
    if (hasPendingActions && audit.team_member?.name && !audit.agent_insights?.auto_resolved_previous) {
      const mostRecent = audit.related_audits.find(r => r.relationship === 'previous');
      if (mostRecent) {
        out.push({
          type: 'warn',
          icon: '⚠️',
          text: `You're auditing ${audit.team_member.name} again. Last audit (${mostRecent.days_gap}d ago) may have unresolved action items. Check Action Tracker for status.`,
        });
      }
    }
  }
  
  // Agent Escalation Recommendation
  if (audit.agent_insights?.escalation_recommended) {
    out.push({
      type: 'warn',
      icon: '🚨',
      text: `Agent recommends escalation: ${audit.agent_insights.comparison_summary || 'Persistent unresolved issues detected'}`,
    });
  }
  
  // Low Resolution Rate Alert
  if (audit.agent_insights?.resolution_rate !== undefined && audit.agent_insights.resolution_rate < 0.3) {
    const rate = Math.round(audit.agent_insights.resolution_rate * 100);
    out.push({
      type: 'warn',
      icon: '📉',
      text: `Low resolution rate (${rate}%) from previous audit. Team member hasn't addressed most issues.`,
    });
  }

  // ===== EXISTING ALERTS =====

  // Forwarded email
  const subject = audit.gmail_source?.subject || audit.email_body_preview || '';
  if (
    subject.toLowerCase().startsWith('fwd:') ||
    subject.toLowerCase().startsWith('fw:') ||
    combined.includes('forwarded')
  ) {
    out.push({
      type: 'info',
      icon: '\ud83d\udce7',
      text: 'This audit was run on a forwarded email. For the most complete data, find and audit the original email from the sender.',
    });
  }

  // Price check limited
  if (
    combined.includes('price') &&
    (combined.includes('unavailable') || combined.includes('not available') || combined.includes('skipped') || combined.includes('last sold price not'))
  ) {
    out.push({
      type: 'warn',
      icon: '\ud83d\udcb0',
      text: 'Price Integrity check is limited \u2014 last sold price was not found in the documents.',
    });
  }

  // Ageing check skipped
  if (combined.includes('ageing') && combined.includes('skipped')) {
    out.push({
      type: 'warn',
      icon: '\ud83d\udce6',
      text: 'Ageing check skipped \u2014 per-SKU age data was not found in the attachments.',
    });
  }

  // Image-only PDF
  if (combined.includes('image-only') || combined.includes('ocr')) {
    out.push({
      type: 'warn',
      icon: '\ud83d\uddbc\ufe0f',
      text: 'One or more PDFs appear to be image-only scans and could not be read. Re-scan or provide a text-based PDF.',
    });
  }

  // Zero extraction from attachments but had files
  if (
    (audit.files?.length > 0) &&
    (combined.includes('no extractable content') || combined.includes('unsupported type'))
  ) {
    out.push({
      type: 'warn',
      icon: '\ud83d\udcc4',
      text: 'Some attachments could not be read. Only the email body was used for the analysis.',
    });
  }

  return out;
}

export default function Results() {
  const { auditId } = useParams();
  const navigate    = useNavigate();
  const { user }    = useAuth();
  const [audit, setAudit]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [activeTab, setActiveTab]     = useState('executive');
  const [activeFilter, setActiveFilter] = useState('ALL'); // drilldown filter
  const [findFilter, setFindFilter]     = useState('ALL'); // findings filter
  const [problemSkus, setProblemSkus] = useState([]); // NEW: repeat offender SKUs
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeInitial, setComposeInitial] = useState({});
  // Auto-thread-refresh: fire on mount when the audit is linked to a Gmail
  // thread, so new replies show up in Mail Thread + AI Progress without the
  // user having to click "Refresh thread".
  const [threadSyncing, setThreadSyncing] = useState(false);

  // Cross-week series continuity
  const [siblings, setSiblings]               = useState(null);   // { siblings: [...] }
  const [crossSeriesLoading, setCrossSeriesLoading] = useState(false);

  useEffect(() => {
    if (!auditId) return;
    setLoading(true);
    
    // Fetch audit and problem SKUs in parallel
    Promise.all([
      fetch(`${BACKEND}/api/audits/${auditId}`, {headers: {Authorization: `Bearer ${localStorage.getItem('access_token')}`}}).then(r => r.ok ? r.json() : null),
      fetch(`${BACKEND}/api/dashboard/problem-skus`, {headers: {Authorization: `Bearer ${localStorage.getItem('access_token')}`}}).then(r => r.ok ? r.json() : []),
    ]).then(([auditData, problemSkusData]) => {
      if (auditData) {
        setAudit(auditData);
        // Silently sync the Gmail thread + AI progress so any reply that
        // landed since the audit was last viewed is captured automatically.
        if (auditData.gmail_thread_id) {
          setThreadSyncing(true);
          fetch(`${BACKEND}/api/audits/${auditId}/refresh-thread`, { method: 'POST' })
            .then(r => r.ok ? r.json() : null)
            .then(data => {
              if (!data) return;
              // Pull the freshly-synced thread + progress back into the audit
              // doc by re-fetching (the refresh endpoint returns a summary,
              // not the full audit, so a refetch keeps everything consistent).
              return fetch(`${BACKEND}/api/audits/${auditId}`).then(r => r.ok ? r.json() : null);
            })
            .then(updated => { if (updated) setAudit(updated); })
            .catch(() => {})
            .finally(() => {
              setThreadSyncing(false);
              // Snapshot the now-current message count so the Action Tracker
              // stops flagging this audit as "new activity since last visit".
              fetch(`${BACKEND}/api/audits/${auditId}/mark-seen`, { method: 'POST' })
                .catch(() => {});
            });
        } else {
          // Even threadless audits should be marked seen so they don't
          // perpetually appear "new" to the tracker.
          fetch(`${BACKEND}/api/audits/${auditId}/mark-seen`, { method: 'POST' })
            .catch(() => {});
        }

        // Pull cross-week series siblings (Samsung W15, W16, W17, …).
        // Cheap Mongo-only call so we always have the progression strip ready.
        fetch(`${BACKEND}/api/audits/${auditId}/series-siblings`)
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d) setSiblings(d); })
          .catch(() => {});
      } else {
        setError('Audit not found or failed to load.');
      }
      if (problemSkusData) setProblemSkus(problemSkusData);
      setLoading(false);
    }).catch(err => {
      setError(err.message);
      setLoading(false);
    });
  }, [auditId]);

  // Helper to check if SKU is a repeat offender
  const isRepeatOffender = (skuModel) => {
    if (!skuModel || problemSkus.length === 0) return false;
    const normalizedModel = skuModel.toLowerCase().replace(/\s+/g, '').substring(0, 50);
    return problemSkus.some(ps => {
      if (!ps.sku) return false;  // Safety check
      const normalizedProblem = ps.sku.toLowerCase().replace(/\s+/g, '').substring(0, 50);
      return normalizedProblem === normalizedModel && ps.reject_count >= 3;
    });
  };

  // ── Build a rich HTML version of the email body with INLINE styles so it
  //     survives Gmail's CSS sanitisation when pasted into a compose window.
  const buildRichEmailHtml = (rawReport, lineItems = [], opts = {}) => {
    if (!rawReport) return { html: '', text: '' };
    const { subject = '', country = '', portfolioAgeing = null } = opts;

    // Use shared cleanup + segment parser (single source of truth)
    const cleaned  = cleanReportText(rawReport);
    const segments = parseEmailDraftSegments(cleaned);

    const escapeHtml = (s) => String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

    // Inline-styled status pill — recognises HIGH ALERT / ALERT / ALLOW
    const statusPill = (val) => {
      const v = (val || '').toUpperCase();
      let bg = '#f1f5f9', border = '#cbd5e1', text = '#334155';
      if (v.includes('HIGH ALERT'))                          { bg = '#fef2f2'; border = '#fca5a5'; text = '#991b1b'; }
      else if (v.includes('ALERT') || v.includes('REVIEW'))  { bg = '#fff7ed'; border = '#fdba74'; text = '#9a3412'; }
      else if (v.includes('REJECT'))                         { bg = '#fef2f2'; border = '#fca5a5'; text = '#991b1b'; }
      else if (v.includes('ALLOW') || v.includes('APPROVE')) { bg = '#f0fdf4'; border = '#86efac'; text = '#166534'; }
      return `<span style="display:inline-block;padding:3px 12px;border-radius:14px;` +
             `font-size:11px;font-weight:700;letter-spacing:0.04em;` +
             `background:${bg};border:1px solid ${border};color:${text};">${escapeHtml(val)}</span>`;
    };

    // Inline-styled WOS / risk pill
    const riskPill = (val, flagged) => {
      const upper = (val || '').toUpperCase();
      let bg = '#f0fdf4', border = '#86efac', text = '#166534';   // OK
      if (flagged || /STAGNANT/.test(upper))      { bg = '#fef2f2'; border = '#fca5a5'; text = '#991b1b'; }
      else if (/HIGH|VERY/.test(upper))           { bg = '#fef2f2'; border = '#fca5a5'; text = '#991b1b'; }
      else if (/MED/.test(upper))                 { bg = '#fffbeb'; border = '#fcd34d'; text = '#92400e'; }
      else if (/NEW/.test(upper))                 { bg = '#eff6ff'; border = '#93c5fd'; text = '#1e40af'; }
      return `<span style="display:inline-block;padding:3px 10px;border-radius:14px;` +
             `font-size:11px;font-weight:600;` +
             `background:${bg};border:1px solid ${border};color:${text};">${escapeHtml(val || '—')}</span>`;
    };

    // Build HTML segment-by-segment using the same parsed structure as the renderer
    const htmlParts = segments.map(seg => {
      if (seg.type === 'text') {
        // Render line-by-line so approval lines get the green inline style
        const inner = seg.value.split('\n').map(ln => {
          const safe = escapeHtml(ln);
          return isApprovalLine(ln)
            ? `<span style="color:#15803d;font-weight:600;">${safe}</span>`
            : safe;
        }).join('\n');
        return `<div style="white-space:pre-wrap;word-break:break-word;margin:0 0 12px;">${inner}</div>`;
      }

      // Pipe-table → styled HTML table. Column kinds use shared getColKind().
      const colKinds = seg.header.map(getColKind);

      const headHtml = seg.header.map(h =>
        `<th style="text-align:left;padding:10px 14px;font-size:11px;font-weight:700;` +
        `text-transform:uppercase;letter-spacing:0.06em;color:#475569;` +
        `border-bottom:1px solid #e2e8f0;background:#f8fafc;">${escapeHtml(h)}</th>`
      ).join('');

      const bodyHtml = seg.rows.map((row, ri) => {
        const cellsHtml = row.map((rawCell, ci) => {
          const flagged = cellHasFlag(rawCell);
          const cell = stripFlag(rawCell);
          const kind = colKinds[ci] || 'text';
          const baseStyle = 'padding:9px 14px;border-top:1px solid #f1f5f9;color:#1e293b;vertical-align:middle;font-size:13px;';

          if (kind === 'status') {
            return `<td style="${baseStyle}">${statusPill(cell)}</td>`;
          }
          if (kind === 'wos') {
            return `<td style="${baseStyle}">${riskPill(cell, flagged)}</td>`;
          }
          if (kind === 'name') {
            return `<td style="${baseStyle}font-family:Menlo,Consolas,monospace;font-size:12px;font-weight:500;max-width:280px;word-break:break-word;">${escapeHtml(cell || '—')}</td>`;
          }
          if (kind === 'stock') {
            return `<td style="${baseStyle}font-variant-numeric:tabular-nums;font-weight:500;color:#334155;">${escapeHtml(cell || '—')}</td>`;
          }
          // ageing / price / generic — flag handling
          const isOk  = /^ok$/i.test(cell);
          const isNa  = /^n\/?a$/i.test(cell);
          if (kind === 'price' && isOk) {
            return `<td style="${baseStyle}color:#15803d;">OK</td>`;
          }
          if (kind === 'price' && isNa) {
            return `<td style="${baseStyle}color:#94a3b8;font-size:11px;">no price data</td>`;
          }
          if (flagged) {
            return `<td style="${baseStyle}color:#b91c1c;font-weight:600;">${escapeHtml(cell || '—')} 🚩</td>`;
          }
          return `<td style="${baseStyle}">${escapeHtml(cell || '—')}</td>`;
        }).join('');
        return `<tr style="background:${ri % 2 === 0 ? '#ffffff' : '#fafbfc'};">${cellsHtml}</tr>`;
      }).join('');

      return (
        `<table style="width:100%;border-collapse:separate;border-spacing:0;` +
        `font-family:'Helvetica Neue',Arial,sans-serif;` +
        `border:1px solid #e2e8f0;border-radius:8px;overflow:hidden;margin:14px 0;">` +
        `<thead><tr>${headHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`
      );
    });

    // ── Portfolio ageing banner (top-of-email gate) ──
    const pa = portfolioAgeing || {};
    const fmtMoney = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const fmtUnits = (v) => Number(v || 0).toLocaleString();
    let portfolioAgeingHtml = '';
    if (pa.blocked) {
      const aged = pa.basis === 'value' ? `$${fmtMoney(pa.total_aged)}` : fmtUnits(pa.total_aged);
      const tot  = pa.basis === 'value' ? `$${fmtMoney(pa.total_stock)}` : fmtUnits(pa.total_stock);
      const subj = pa.basis === 'value' ? 'closing stock value' : 'units across the inventory';
      const week = pa.week ? ` (${pa.week})` : '';
      portfolioAgeingHtml =
        `<div style="background:#fef2f2;border:2px solid #dc2626;border-radius:12px;` +
        `padding:14px 18px;margin:0 0 16px;font-family:'Helvetica Neue',Arial,sans-serif;">` +
        `<div style="font-weight:800;font-size:14px;color:#991b1b;letter-spacing:0.02em;">` +
        `REORDER BLOCKED — Portfolio Ageing ${pa.pct}%${week} (threshold ${pa.threshold_pct || 10}%)</div>` +
        `<div style="font-size:12px;color:#b91c1c;margin-top:3px;">` +
        `${aged} aged / ${tot} ${subj}. ` +
        `Liquidate aged stock before placing this order.</div>` +
        `</div>`;
    } else if (pa.has_data) {
      const subj = pa.basis === 'value' ? 'closing stock value' : 'inventory';
      const week = pa.week ? ` (${pa.week})` : '';
      portfolioAgeingHtml =
        `<div style="background:#f0fdf4;border:1px solid #86efac;border-radius:12px;` +
        `padding:8px 14px;margin:0 0 12px;font-size:12px;color:#166534;` +
        `font-family:'Helvetica Neue',Arial,sans-serif;">` +
        `<span style="font-weight:600;margin-right:6px;">Portfolio ageing OK:</span>` +
        `${pa.pct}% of ${subj} aged &gt;60 days${week} (threshold ${pa.threshold_pct || 10}%)</div>`;
    }

    // ── Subject header (mirrors in-app rendering) ──
    let subjectHeaderHtml = '';
    if (subject) {
      const subjectLine =
        `<div style="font-size:13px;color:#0f172a;margin:0;">` +
        `<span style="font-weight:700;color:#475569;margin-right:6px;">Subject:</span>` +
        `<span>${escapeHtml(subject)}</span></div>`;
      subjectHeaderHtml =
        `<div style="background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;` +
        `padding:12px 18px;margin:0 0 16px;font-family:'Helvetica Neue',Arial,sans-serif;">` +
        subjectLine + `</div>`;
    }

    // ── Summary banner — tier-based (matches in-app banner) ──
    const breakdownItemsLocal = (lineItems || []).filter(it => it.recommendation === 'REJECT');
    const highAlertItemsLocal = (lineItems || []).filter(it => getAlertTier(it) === 'HIGH_ALERT');
    const alertItemsLocal     = (lineItems || []).filter(it => getAlertTier(it) === 'ALERT');
    const totalSKUsLocal      = lineItems?.length || 0;

    let bannerHtml = '';
    if (highAlertItemsLocal.length > 0 || alertItemsLocal.length > 0) {
      const isHigh = highAlertItemsLocal.length > 0;
      const bg     = isHigh ? '#fef2f2' : '#fff7ed';
      const bord   = isHigh ? '#fca5a5' : '#fdba74';
      const titleC = isHigh ? '#991b1b' : '#9a3412';
      const subC   = isHigh ? '#b91c1c' : '#c2410c';
      const title  = isHigh
        ? `${highAlertItemsLocal.length} HIGH ALERT ${highAlertItemsLocal.length !== 1 ? 'items' : 'item'} — Immediate Action Required`
        : `${alertItemsLocal.length} ALERT ${alertItemsLocal.length !== 1 ? 'items' : 'item'} — Review Required`;
      const sub = `${totalSKUsLocal} total SKUs audited.` +
        (isHigh && alertItemsLocal.length > 0
          ? ` ${alertItemsLocal.length} additional ALERT item(s) for review.`
          : '');
      bannerHtml =
        `<div style="background:${bg};border:1.5px solid ${bord};border-radius:12px;` +
        `padding:14px 18px;margin:0 0 16px;font-family:'Helvetica Neue',Arial,sans-serif;">` +
        `<div style="font-weight:700;font-size:14px;color:${titleC};">${escapeHtml(title)}</div>` +
        `<div style="font-size:12px;color:${subC};margin-top:3px;">${escapeHtml(sub)}</div>` +
        `</div>`;
    } else if (totalSKUsLocal > 0) {
      bannerHtml =
        `<div style="background:#f0fdf4;border:1.5px solid #86efac;border-radius:12px;` +
        `padding:12px 18px;margin:0 0 16px;color:#15803d;font-weight:600;font-size:14px;` +
        `font-family:'Helvetica Neue',Arial,sans-serif;">` +
        `All ${totalSKUsLocal} SKUs within thresholds — reorder approved.</div>`;
    }

    // ── Alert items detailed breakdown (matches in-app cards) ──
    let alertCardsHtml = '';
    if (breakdownItemsLocal.length > 0) {
      const cards = breakdownItemsLocal.map(item => {
        const tier = getAlertTier(item);
        const tierLabel = tier === 'HIGH_ALERT' ? 'HIGH ALERT' : 'ALERT';
        const primaryFlag = (item.flags || [])[0] || 'HIGH_WOS';
        const fc = FLAG_COLORS[primaryFlag] || FLAG_COLORS['HIGH_WOS'];
        const metric = (label, value, color) =>
          `<td style="padding:4px 8px 4px 0;vertical-align:top;">` +
          `<div style="font-size:10px;color:#94a3b8;margin:0 0 2px;">${escapeHtml(label)}</div>` +
          `<div style="font-size:13px;font-weight:700;color:${color};` +
          `font-variant-numeric:tabular-nums;">${escapeHtml(String(value))}</div></td>`;

        const wosVal   = item.wos_label || '—';
        const stockVal = (item.current_stock || 0).toLocaleString();
        const salesVal = (item.sales_last_4w || 0).toLocaleString();
        const fourth = item.price_variance != null
          ? ['Price Δ', `${item.price_variance >= 0 ? '+' : ''}${Number(item.price_variance).toFixed(2)}`, '#b45309']
          : ['Ageing', `${item.ageing_pct || 0}%`, '#6d28d9'];

        const reasonHtml = item.recommendation_reason
          ? `<div style="padding:8px 14px 10px;border-top:1px solid ${fc.border};` +
            `font-size:11px;color:#64748b;font-style:italic;">${escapeHtml(item.recommendation_reason)}</div>`
          : '';

        return (
          `<div style="border:2px solid ${fc.border};border-radius:12px;background:#ffffff;` +
          `overflow:hidden;margin:0 0 8px;font-family:'Helvetica Neue',Arial,sans-serif;">` +
            `<div style="background:${fc.bg};padding:10px 14px;border-bottom:1px solid ${fc.border};">` +
              `<table style="width:100%;border-collapse:collapse;"><tr>` +
                `<td style="font-family:'IBM Plex Mono',Menlo,Consolas,monospace;font-size:12px;` +
                `font-weight:600;color:#0f172a;">${escapeHtml(item.model || '—')}</td>` +
                `<td style="text-align:right;width:1%;white-space:nowrap;">` +
                  `<span style="font-size:10px;font-weight:800;color:${fc.text};` +
                  `background:#ffffff;border:1px solid ${fc.border};border-radius:20px;padding:2px 8px;` +
                  `letter-spacing:0.04em;margin-right:6px;">${escapeHtml(tierLabel)}</span>` +
                  `<span style="font-size:10px;font-weight:600;color:${fc.text};` +
                  `background:#ffffff;border:1px solid ${fc.border};border-radius:20px;padding:2px 8px;">` +
                  `${escapeHtml(fc.label)}</span>` +
                `</td>` +
              `</tr></table>` +
            `</div>` +
            `<div style="padding:10px 14px;">` +
              `<table style="width:100%;border-collapse:collapse;"><tr>` +
                metric('WOS',        wosVal,   fc.text) +
                metric('Stock',      stockVal, '#334155') +
                metric('Sales (4w)', salesVal, '#334155') +
                metric(fourth[0],    fourth[1], fourth[2]) +
              `</tr></table>` +
            `</div>` +
            reasonHtml +
          `</div>`
        );
      }).join('');

      alertCardsHtml =
        `<div style="margin:18px 0 0;font-family:'Helvetica Neue',Arial,sans-serif;">` +
        `<div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:0.08em;` +
        `color:#94a3b8;margin:0 0 8px;">Alert Items — Detailed Breakdown</div>` +
        cards +
        `</div>`;
    }

    const html =
      `<div style="font-family:'Helvetica Neue',Arial,sans-serif;font-size:14px;` +
      `line-height:1.6;color:#1e293b;">${subjectHeaderHtml}${portfolioAgeingHtml}${bannerHtml}${htmlParts.join('')}${alertCardsHtml}</div>`;

    // Plain-text fallback should also include subject + portfolio ageing + alert breakdown
    let plainText = '';
    if (subject) plainText += `Subject: ${subject}\n`;
    if (plainText) plainText += '\n';
    if (pa.blocked) {
      const aged = pa.basis === 'value' ? `$${fmtMoney(pa.total_aged)}` : fmtUnits(pa.total_aged);
      const tot  = pa.basis === 'value' ? `$${fmtMoney(pa.total_stock)}` : fmtUnits(pa.total_stock);
      const week = pa.week ? ` (${pa.week})` : '';
      plainText += `*** REORDER BLOCKED — Portfolio Ageing ${pa.pct}%${week} (threshold ${pa.threshold_pct || 10}%) ***\n`;
      plainText += `${aged} aged / ${tot} ${pa.basis === 'value' ? 'closing stock value' : 'units across the inventory'}.\n\n`;
    } else if (pa.has_data) {
      const week = pa.week ? ` (${pa.week})` : '';
      plainText += `Portfolio ageing OK: ${pa.pct}% of ${pa.basis === 'value' ? 'closing stock value' : 'inventory'} aged >60 days${week} (threshold ${pa.threshold_pct || 10}%)\n\n`;
    }
    plainText += cleaned;
    if (breakdownItemsLocal.length > 0) {
      plainText += '\n\nAlert Items — Detailed Breakdown\n';
      breakdownItemsLocal.forEach(item => {
        const tier = getAlertTier(item);
        const tierLabel = tier === 'HIGH_ALERT' ? 'HIGH ALERT' : 'ALERT';
        const flagLabel = (FLAG_COLORS[(item.flags || [])[0]] || FLAG_COLORS['HIGH_WOS']).label;
        plainText += `\n• ${item.model || '—'} [${tierLabel} · ${flagLabel}]\n`;
        plainText += `  WOS: ${item.wos_label || '—'} | Stock: ${(item.current_stock || 0).toLocaleString()} | Sales (4w): ${(item.sales_last_4w || 0).toLocaleString()}`;
        if (item.price_variance != null) {
          plainText += ` | Price Δ: ${item.price_variance >= 0 ? '+' : ''}${Number(item.price_variance).toFixed(2)}`;
        } else {
          plainText += ` | Ageing: ${item.ageing_pct || 0}%`;
        }
        if (item.recommendation_reason) {
          plainText += `\n  ${item.recommendation_reason}`;
        }
        plainText += '\n';
      });
    }

    return { html, text: plainText };
  };

  // Build rich HTML for either Copy or Send
  const buildEmailHtmlAndText = () => {
    if (!audit?.audit_report) return { html: '', text: '' };
    const lineItems = audit?.analysis?.line_items || [];
    return buildRichEmailHtml(audit.audit_report, lineItems, {
      subject: audit?.email_subject,
      country: audit?.country,
      portfolioAgeing: audit?.analysis?.portfolio_ageing,
    });
  };

  const openCompose = () => {
    const { html } = buildEmailHtmlAndText();
    const { all: threadParticipants, lastFrom } = extractThreadParticipants(audit, user?.email);
    // Default To = last sender of the thread, fallback to the team-member contact
    const defaultTo = lastFrom || parseAddress(audit?.gmail_source?.from) || audit?.team_member?.email || '';
    // Default Cc = every other participant
    const defaultCc = threadParticipants.filter(p => p !== defaultTo);
    setComposeInitial({
      to:           defaultTo,
      cc:           defaultCc,
      subject:      audit?.email_subject || `Supply Chain Audit Report — ${audit?.country || ''}`.trim(),
      body_html:    html,
      thread_id:    audit?.gmail_thread_id || '',
      audit_id:     audit?._id,
      thread_participants: threadParticipants,
    });
    setComposeOpen(true);
  };

  const copyReport = async () => {
    if (!audit?.audit_report) return;
    const { html, text } = buildEmailHtmlAndText();
    try {
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        const item = new ClipboardItem({
          'text/html':  new Blob([html], { type: 'text/html'  }),
          'text/plain': new Blob([text], { type: 'text/plain' }),
        });
        await navigator.clipboard.write([item]);
        toast.success('Copied with formatting — paste into Gmail to keep the table & badges');
        return;
      }
    } catch (err) {
      // Will fall back below
    }

    // Fallback path — execCommand('copy') from a hidden contenteditable
    try {
      const div = document.createElement('div');
      div.contentEditable = 'true';
      div.style.position = 'fixed';
      div.style.left = '-9999px';
      div.innerHTML = html;
      document.body.appendChild(div);
      const range = document.createRange();
      range.selectNodeContents(div);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
      document.execCommand('copy');
      sel.removeAllRanges();
      div.remove();
      toast.success('Copied with formatting — paste into Gmail to keep the table & badges');
    } catch (err) {
      // Last-resort plain text
      navigator.clipboard.writeText(text);
      toast.success('Copied as plain text');
    }
  };
  const downloadReport = () => {
    if (!audit?.audit_report) return;
    const blob = new Blob([audit.audit_report], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `audit-${auditId?.slice(-8)}.txt`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <ResultsSkeleton />;
  if (error) return (
    <div className="page-pad">
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>Failed to load audit: {error}</AlertDescription>
      </Alert>
    </div>
  );

  const summaryRaw = audit?.analysis?.summary || {};
  const lineItems  = audit?.analysis?.line_items || [];
  const total      = summaryRaw.total_skus || 0;

  // Tier counts derived from line items (single source of truth via getAlertTier)
  const alertCount     = lineItems.filter(i => getAlertTier(i) === 'ALERT').length;
  const highAlertCount = lineItems.filter(i => getAlertTier(i) === 'HIGH_ALERT').length;
  const summary = {
    ...summaryRaw,
    alert:      alertCount,
    high_alert: highAlertCount,
  };

  // Table rows filtered by the drilldown card (tier-based)
  const tableRows = activeFilter === 'ALL'
    ? lineItems
    : activeFilter === 'ALLOW'
      ? lineItems.filter(i => getAlertTier(i) === 'ALLOW')
      : lineItems.filter(i => getAlertTier(i) === activeFilter);

  // Findings tab rows
  const findRows = findFilter === 'ALL'
    ? lineItems
    : findFilter === 'PRICED'
      ? lineItems.filter(i => i.pi_price != null && i.pi_price > 0)
      : findFilter === 'ALLOW'
        ? lineItems.filter(i => getAlertTier(i) === 'ALLOW')
        : lineItems.filter(i => getAlertTier(i) === findFilter);

  // Finding counts
  const findCounts = {
    ALL:        lineItems.length,
    ALLOW:      lineItems.filter(i => getAlertTier(i) === 'ALLOW').length,
    ALERT:      alertCount,
    HIGH_ALERT: highAlertCount,
    PRICED:     lineItems.filter(i => i.pi_price != null && i.pi_price > 0).length,
  };

  // Chart data — tier-based
  const recData = [
    { name: 'ALLOW',      value: summary.allow      || 0, color: '#059669' },
    { name: 'ALERT',      value: alertCount,             color: '#ea580c' },
    { name: 'HIGH ALERT', value: highAlertCount,         color: '#dc2626' },
  ].filter(d => d.value > 0);
  const wosData = [
    { name: 'OK',       value: lineItems.filter(i => i.wos_grade === 'OK').length,       color: '#059669' },
    { name: 'CHECK',    value: lineItems.filter(i => i.wos_grade === 'CHECK').length,    color: '#d97706' },
    { name: 'FLAG',     value: lineItems.filter(i => i.wos_grade === 'FLAG').length,     color: '#dc2626' },
    { name: 'STAGNANT', value: lineItems.filter(i => i.wos_grade === 'STAGNANT').length, color: '#18181b' },
  ].filter(d => d.value > 0);

  const hasAgentData = audit?.agent_actions?.length > 0 || audit?.discrepancies?.length > 0;
  const hasThread    = !!audit?.gmail_thread_id;
  const visibleTabs  = TABS.filter(t => {
    if (t.key === 'log' && !hasAgentData) return false;
    if ((t.key === 'thread' || t.key === 'progress') && !hasThread) return false;
    return true;
  });

  // Handle card click: if same filter clicked again, reset to ALL
  const handleCardClick = (key) => {
    const next = activeFilter === key ? 'ALL' : key;
    setActiveFilter(next);
    // Switch to executive tab so the filtered table is visible
    setActiveTab('executive');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="page-pad max-w-[1200px]"
    >
      {/* Page header */}
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}
            className="gap-1.5 -ml-2 h-8 mb-1">
            <ArrowLeft className="w-3.5 h-3.5" />Back
          </Button>
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight"
            style={{ fontFamily: 'Space Grotesk' }}>Audit Results</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {audit?.created_at ? new Date(audit.created_at).toLocaleString() : ''}
            {audit?.files?.length > 0 && (
              <span className="ml-2">· {audit.files.length} file{audit.files.length > 1 ? 's' : ''}</span>
            )}
            {audit?.processing_time_ms && (
              <span className="ml-2">· {(audit.processing_time_ms / 1000).toFixed(1)}s</span>
            )}
          </p>
        </div>
      </div>

      {/* ─ DRILLDOWN STAT CARDS ─ */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {STAT_CARDS.map(({ key, label, valueKey, icon: Icon, iconBg, iconColor, numColor, ring, activeBg, bar }) => {
          const value    = summary[valueKey] ?? 0;
          const isActive = activeFilter === key;
          const pct      = total > 0 && key !== 'ALL' ? Math.round((value / total) * 100) : null;

          return (
            <motion.div
              key={key}
              whileTap={{ scale: 0.97 }}
              onClick={() => handleCardClick(key)}
              data-testid={`stat-card-${key.toLowerCase()}`}
              role="button" tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && handleCardClick(key)}
              className={`relative rounded-2xl border-2 p-4 cursor-pointer select-none
                transition-all duration-200 hover:shadow-md
                ${isActive
                  ? `${activeBg} shadow-md ring-2 ${ring} ring-offset-1`
                  : 'bg-white border-border/70 hover:border-border'}`}
            >
              {/* Active indicator dot */}
              {isActive && key !== 'ALL' && (
                <div className="absolute top-3 right-3 w-2 h-2 rounded-full"
                  style={{ background: bar?.color }} />
              )}

              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    {label}
                  </p>
                </div>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
                  <Icon className={`w-4 h-4 ${iconColor}`} />
                </div>
              </div>

              <p className={`text-3xl font-bold tabular-nums leading-none ${numColor}`}
                style={{ fontFamily: 'Space Grotesk' }}>
                {value.toLocaleString()}
              </p>

              {/* Progress bar for ALLOW/REVIEW/REJECT */}
              {pct !== null && bar && (
                <div className="mt-2.5">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] text-muted-foreground">{pct}% of total</span>
                  </div>
                  <div className="h-1 rounded-full bg-border overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: bar.color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.5, ease: 'easeOut' }}
                    />
                  </div>
                </div>
              )}

              {/* Clickable hint */}
              {!isActive && (
                <p className="text-[10px] text-muted-foreground/60 mt-1.5">
                  {key === 'ALL' ? 'Show all' : `Filter to ${key}`}
                </p>
              )}
              {isActive && (
                <p className="text-[10px] font-medium mt-1.5" style={{ color: bar?.color || 'hsl(var(--primary))' }}>
                  ✓ Filtering table · click to clear
                </p>
              )}
            </motion.div>
          );
        })}
      </div>

      {/* Active filter banner */}
      <AnimatePresence>
        {activeFilter !== 'ALL' && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-accent border border-primary/20 text-xs"
          >
            <BarChart3 className="w-3.5 h-3.5 text-primary" />
            <span className="text-primary font-medium">
              Showing <strong>{tableRows.length}</strong> {activeFilter} items out of {total}
            </span>
            <button onClick={() => setActiveFilter('ALL')}
              className="ml-auto flex items-center gap-1 text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" /> Clear
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Smart Warnings — plain English, business-relevant only */}
      {(() => {
        const sw = getSmartWarnings(audit?.warnings, audit);
        if (!sw.length) return null;
        return (
          <div className="mb-5 space-y-2.5">
            {sw.map((w, i) => (
              <div key={i} className={`rounded-xl border overflow-hidden ${
                w.type === 'success' 
                  ? 'border-emerald-200 bg-emerald-50/60'
                  : w.type === 'info' 
                  ? 'border-blue-200 bg-blue-50/60'
                  : w.type === 'prompt'
                  ? 'border-orange-200 bg-orange-50/60'
                  : 'border-amber-200 bg-amber-50/60'
              }`}>
                <div className={`flex items-start gap-3 px-4 py-3 text-sm ${
                  w.type === 'success' 
                    ? 'text-emerald-900'
                    : w.type === 'info' 
                    ? 'text-blue-800' 
                    : w.type === 'prompt'
                    ? 'text-orange-900'
                    : 'text-amber-900'
                }`}>
                  <span className="text-base flex-shrink-0 mt-0.5">{w.icon}</span>
                  <div className="flex-1">
                    <span>{w.text}</span>
                    
                    {/* Action buttons for prompts */}
                    {w.actions && w.actions.length > 0 && (
                      <div className="flex gap-2 mt-2">
                        {w.actions.map((action, idx) => (
                          <Button
                            key={idx}
                            size="sm"
                            variant={action.action === 'escalate' ? 'destructive' : 'default'}
                            className="h-7 text-xs"
                            onClick={async () => {
                              try {
                                const res = await fetch(
                                  `${BACKEND}/api/audits/${action.auditId}/action`,
                                  {
                                    method: 'PATCH',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ status: action.action === 'resolve' ? 'resolved' : 'escalated' }),
                                  }
                                );
                                if (res.ok) {
                                  toast.success(`Previous audit marked as ${action.action === 'resolve' ? 'Resolved' : 'Escalated'}`);
                                  window.location.reload();
                                }
                              } catch (err) {
                                toast.error('Failed to update status');
                              }
                            }}
                          >
                            {action.label}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* ─ AGENT INSIGHTS (Comparison) ─ */}
      {audit?.agent_insights && (
        <AgentInsights insights={audit.agent_insights} />
      )}

      {/* ─ ACTION PLAN ─ */}
      {audit?.gmail_source && (
        <div className="mb-5">
          <ActionPlan audit={audit} onUpdate={() => window.location.reload()} />
        </div>
      )}

      {/* ─ SERIES PROGRESSION (cross-week continuity) ─ */}
      {siblings?.has_series && (
        <div className="mb-5">
          <SeriesProgressionCard auditId={audit?._id} siblings={siblings} />
        </div>
      )}

      {/* ─ RELATED AUDITS ─ */}
      {audit?.team_member?.email && (
        <div className="mb-5">
          <RelatedAudits 
            currentAuditId={audit._id} 
            teamMemberEmail={audit.team_member.email}
          />
        </div>
      )}

      {/* ─ CLASSIC TABS ─ */}
      <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
        <ClassicTabs
          active={activeTab}
          setActive={setActiveTab}
          tabs={visibleTabs}
          agentCount={audit?.agent_actions?.length}
          discCount={audit?.discrepancies?.length}
        />

        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15 }}
          >

            {/* ── EXECUTIVE SUMMARY ── */}
            {activeTab === 'executive' && (
              <div className="p-5 space-y-5">
                {/* Filtered table */}
                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="audit-table w-full" data-testid="audit-summary-table">
                    <thead>
                      <tr className="bg-secondary/60 border-b border-border">
                        <th className="text-left px-4 py-3">SKU / Model</th>
                        <th className="text-left px-4 py-3">WOS Grade</th>
                        <th className="hidden sm:table-cell text-left px-4 py-3">Ageing %</th>
                        <th className="hidden sm:table-cell text-left px-4 py-3">Price Δ</th>
                        <th className="text-left px-4 py-3">Decision</th>
                      </tr>
                    </thead>
                    <tbody>
                      {/* rows - no AnimatePresence inside tbody (breaks React) */}
                        {tableRows.map((item, idx) => (
                          <motion.tr
                            key={item.model + idx}
                            initial={{ opacity: 0, x: -4 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0 }}
                            transition={{ delay: idx * 0.01, duration: 0.15 }}
                            className="border-b border-border/50 last:border-0 hover:bg-accent/40 transition-colors"
                          >
                            <td className="px-4 py-3">
                              <span className="model-cell truncate-cell font-mono text-xs font-medium block max-w-[200px] sm:max-w-[260px]" title={item.model}>
                                {item.model}
                              </span>
                            </td>
                            <td className="px-4 py-3"><RiskBadge grade={item.wos_grade} label={item.wos_label} /></td>
                            <td className="hidden sm:table-cell px-4 py-3 tabular-nums text-sm">
                              <span className={item.ageing_flag ? 'text-red-600 font-semibold' : ''}>
                                {item.ageing_pct}%{item.ageing_flag ? ' 🚩' : ''}
                              </span>
                            </td>
                            <td className="hidden sm:table-cell px-4 py-3 tabular-nums text-sm">
                              {item.price_variance != null
                                ? <span className={item.price_flag ? 'text-red-600 font-semibold' : 'text-emerald-700'}>
                                    {item.price_variance >= 0 ? '+' : ''}{item.price_variance?.toFixed(2)}{item.price_flag ? ' 🚩' : ''}
                                  </span>
                                : (item.pi_price != null && item.pi_price > 0)
                                ? <span className="flex items-center gap-1.5">
                                    <span className="text-blue-600 font-medium" title="PI price extracted (no reference price to compare)">
                                      ${Number(item.pi_price).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}
                                    </span>
                                    <TooltipProvider delayDuration={100}>
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <Info className="w-3 h-3 text-amber-500 cursor-help flex-shrink-0" />
                                        </TooltipTrigger>
                                        <TooltipContent side="top" className="max-w-[240px] text-xs">
                                          <p>No reference price in source documents to compare against.</p>
                                        </TooltipContent>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </span>
                                : <span className="text-muted-foreground text-xs">no price data</span>}
                            </td>
                            <td className="px-4 py-3"><RecommendationBadge item={item} /></td>
                          </motion.tr>
                        ))}
                      {tableRows.length === 0 && (
                        <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">No items match</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Mini charts */}
                <div className="hidden sm:grid grid-cols-2 gap-4">
                  <Card className="border-border/60">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Recommendation Split</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={110}>
                        <BarChart data={recData} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                          <Bar dataKey="value" radius={[4,4,0,0]}>
                            {recData.map((e, i) => <Cell key={i} fill={e.color} />)}
                          </Bar>
                          <ChartTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }}
                            formatter={(v,n,p) => [v, p.payload.name]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                  <Card className="border-border/60">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">WOS Distribution</CardTitle></CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={110}>
                        <PieChart>
                          <Pie data={wosData} cx="50%" cy="50%" innerRadius={26} outerRadius={48} dataKey="value">
                            {wosData.map((e, i) => <Cell key={i} fill={e.color} />)}
                          </Pie>
                          <ChartTooltip contentStyle={{ fontSize: 11, borderRadius: 8 }}
                            formatter={(v,n,p) => [v + ' SKU(s)', p.payload.name]} />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </div>
              </div>
            )}

            {/* ── DETAILED FINDINGS ── */}
            {activeTab === 'findings' && (
              <div className="p-5 space-y-4">
                {/* Filter pills */}
                <div className="flex items-center gap-2 flex-wrap">
                  {['ALL','ALLOW','ALERT','HIGH_ALERT','PRICED'].map(fk => (
                    <button key={fk}
                      onClick={() => setFindFilter(fk)}
                      className={`filter-pill ${
                        findFilter === fk
                          ? fk === 'ALL'         ? 'active-all'
                          : fk === 'ALLOW'       ? 'active-allow'
                          : fk === 'ALERT'       ? 'active-review'
                          : fk === 'HIGH_ALERT'  ? 'active-reject'
                          : 'active-allow'  // PRICED uses green style
                          : ''
                      }`}>
                      {fk === 'ALL' ? 'All' : fk === 'PRICED' ? '💰 Has Price' : fk === 'HIGH_ALERT' ? 'HIGH ALERT' : fk}
                      <span className="pill-count">{findCounts[fk]}</span>
                    </button>
                  ))}
                </div>

                <AnimatePresence mode="popLayout">
                  {findRows.length === 0
                    ? <p className="text-sm text-muted-foreground text-center py-8">No items</p>
                    : findRows.map((item, idx) => {
                      const isRepeat = isRepeatOffender(item.model);
                      const itemTier = getAlertTier(item);
                      return (
                      <motion.div key={item.model + idx}
                        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }} transition={{ delay: idx * 0.025 }}
                      >
                        <Card className={`border-l-4 ${
                          itemTier === 'HIGH_ALERT' ? 'border-l-red-400'
                          : itemTier === 'ALERT'    ? 'border-l-orange-400'
                          : 'border-l-emerald-400'
                        } ${isRepeat ? 'ring-2 ring-red-500/30' : ''}`}>
                          <CardHeader className="pb-2">
                            <div className="flex items-start justify-between flex-wrap gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <CardTitle className="text-sm font-mono truncate">{item.model}</CardTitle>
                                  {isRepeat && (
                                    <Badge variant="destructive" className="h-4 text-[10px]">
                                      🔁 Repeat Offender
                                    </Badge>
                                  )}
                                </div>
                                <CardDescription className="text-xs mt-0.5">
                                  Stock: {item.current_stock?.toLocaleString()} · Sales (4w): {item.sales_last_4w?.toLocaleString()}
                                </CardDescription>
                              </div>
                              <RecommendationBadge item={item} />
                            </div>
                          </CardHeader>
                          <CardContent>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                              <div className="bg-secondary/50 rounded-lg p-3">
                                <p className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />Weeks of Stock
                                </p>
                                <RiskBadge grade={item.wos_grade} label={item.wos_label} />
                                <p className="text-xs text-muted-foreground mt-1">{item.wos_risk}</p>
                              </div>
                              <div className={`rounded-lg p-3 ${item.ageing_flag ? 'bg-red-50 border border-red-100' : 'bg-secondary/50'}`}>
                                <p className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                                  <Package className="w-3 h-3" />Ageing
                                </p>
                                <p className={`text-sm font-bold tabular-nums ${item.ageing_flag ? 'text-red-700' : 'text-foreground'}`}>
                                  {item.ageing_pct}% &gt;60 days
                                </p>
                                {item.ageing_flag && <p className="text-xs text-red-600 mt-0.5">🚩 Exceeds 10%</p>}
                              </div>
                              <div className={`rounded-lg p-3 ${item.price_flag ? 'bg-red-50 border border-red-100' : 'bg-secondary/50'}`}>
                                <p className="text-xs font-semibold text-muted-foreground mb-1 flex items-center gap-1">
                                  <DollarSign className="w-3 h-3" />Price
                                </p>
                                {/* Show pi_price prominently when available */}
                                {(item.pi_price != null && item.pi_price > 0) ? (
                                  <div>
                                    <p className="text-sm font-bold text-foreground tabular-nums flex items-center gap-1.5">
                                      PI: ${Number(item.pi_price).toLocaleString('en-US', {minimumFractionDigits:2, maximumFractionDigits:2})}
                                      {(!item.last_sold_price || item.last_sold_price === 0) && (
                                        <TooltipProvider delayDuration={100}>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <Info className="w-3.5 h-3.5 text-amber-500 cursor-help flex-shrink-0" />
                                            </TooltipTrigger>
                                            <TooltipContent side="top" className="max-w-[260px] text-xs">
                                              <p className="font-medium mb-1">⚠️ Price check incomplete</p>
                                              <p>Your source documents contain the PI price but no "Last Sold Price" or "Reference Price" column to compare against. Cannot flag price increases.</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </p>
                                    {item.last_sold_price != null && item.last_sold_price > 0 ? (
                                      <p className="text-xs text-muted-foreground mt-0.5">{item.price_note}</p>
                                    ) : (
                                      <p className="text-xs text-amber-600 mt-0.5">No reference price to compare</p>
                                    )}
                                    {item.price_flag && <p className="text-xs text-red-600 mt-0.5">🚩 PI exceeds last sold</p>}
                                  </div>
                                ) : (
                                  <p className="text-xs text-muted-foreground">{item.price_note || 'No price data in document'}</p>
                                )}
                              </div>
                            </div>
                            {item.flags?.length > 0 && (
                              <div className="mt-2.5 flex flex-wrap gap-1.5">
                                {item.flags.map(flag => (
                                  <span key={flag}
                                    className="inline-flex items-center px-2 py-0.5 text-xs font-mono rounded bg-zinc-100 text-zinc-700 border border-zinc-200">
                                    {flag}
                                  </span>
                                ))}
                              </div>
                            )}
                            <p className="text-xs text-muted-foreground mt-2 italic">{item.recommendation_reason}</p>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                    })
                  }
                </AnimatePresence>
              </div>
            )}

            {/* ── EMAIL DRAFT ── */}
            {activeTab === 'draft' && (
              <div className="p-5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <h3 className="text-sm font-semibold">Audit Email Draft</h3>
                    <p className="text-xs text-muted-foreground">
                      ALERT &amp; HIGH ALERT items · copy with full formatting
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {(() => {
                      const sentCount = audit?.email_sent_count
                        || (audit?.email_sent_history?.length ?? 0)
                        || (audit?.email_sent ? 1 : 0);
                      const alreadySent = sentCount > 0;
                      return (
                        <Button
                          variant={alreadySent ? "outline" : "default"}
                          size="sm"
                          data-testid="audit-email-send-button"
                          onClick={openCompose}
                          className={`gap-1.5 h-8 text-xs ${alreadySent ? 'border-primary/40 text-primary hover:bg-primary/5' : ''}`}
                        >
                          <Send className="w-3.5 h-3.5" />
                          {alreadySent
                            ? `Resend${sentCount > 0 ? ` · ${sentCount} sent` : ''}`
                            : 'Send via Gmail'}
                        </Button>
                      );
                    })()}
                    <Button variant="outline" size="sm" data-testid="audit-email-copy-button"
                      onClick={copyReport} className="gap-1.5 h-8 text-xs">
                      <Copy className="w-3.5 h-3.5" />Copy
                    </Button>
                    <Button variant="outline" size="sm" onClick={downloadReport}
                      className="gap-1.5 h-8 text-xs">
                      <Download className="w-3.5 h-3.5" />Download
                    </Button>
                  </div>
                </div>

                {/* Send history strip */}
                {(() => {
                  const history = audit?.email_sent_history
                    || (audit?.email_sent ? [audit.email_sent] : []);
                  if (!history.length) return null;
                  return (
                    <div className="mb-4 rounded-lg border border-emerald-200/70 bg-emerald-50/40 px-3 py-2.5" data-testid="email-sent-history">
                      <div className="flex items-center gap-2 mb-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        <span className="text-xs font-semibold text-emerald-800">
                          {history.length} email{history.length > 1 ? 's' : ''} sent for this audit
                        </span>
                      </div>
                      <ul className="space-y-1 ml-5">
                        {history.slice(-3).reverse().map((h, idx) => (
                          <li key={idx} className="text-[11px] text-emerald-900/80 flex items-center gap-2 flex-wrap">
                            <span className="font-mono">
                              {new Date(h.sent_at).toLocaleString(undefined, {
                                month: 'short', day: 'numeric',
                                hour: '2-digit', minute: '2-digit',
                              })}
                            </span>
                            <span>·</span>
                            <span>to <span className="font-medium">{h.to}</span></span>
                            {h.cc && <span className="text-emerald-700/70">+ cc {h.cc}</span>}
                          </li>
                        ))}
                        {history.length > 3 && (
                          <li className="text-[11px] text-emerald-700/70 italic">
                            (+{history.length - 3} earlier)
                          </li>
                        )}
                      </ul>
                    </div>
                  );
                })()}

                <Separator className="mb-4" />

                <EmailDraftRenderer
                  report={audit?.audit_report}
                  lineItems={lineItems}
                  subject={audit?.email_subject}
                  country={audit?.country}
                  portfolioAgeing={audit?.analysis?.portfolio_ageing}
                />
              </div>
            )}

            {/* ── MAIL THREAD ── */}
            {activeTab === 'thread' && (
              <div className="p-5">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <Mail className="w-4 h-4 text-primary" />Full Mail Thread
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {audit?.thread?.message_count || 0} message{(audit?.thread?.message_count || 0) !== 1 ? 's' : ''} ·
                      {' '}from "{audit?.thread_subject || audit?.gmail_source?.subject || '—'}"
                    </p>
                  </div>
                  {threadSyncing && (
                    <div
                      className="flex items-center gap-2 text-[11px] text-primary"
                      data-testid="thread-syncing-indicator"
                    >
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      Syncing latest replies…
                    </div>
                  )}
                </div>
                <Separator className="mb-4" />
                <ThreadTimeline thread={audit?.thread} />
              </div>
            )}

            {/* ── AI PROGRESS SUMMARY ── */}
            {activeTab === 'progress' && (
              <div className="p-5">
                {threadSyncing && (
                  <div
                    className="mb-4 flex items-center gap-2 text-[11px] text-primary"
                    data-testid="progress-syncing-indicator"
                  >
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Syncing latest replies — AI is re-analysing the thread…
                  </div>
                )}
                <ProgressSummaryPanel
                  audit={audit}
                  onRefreshed={(data) => {
                    // Mutate local audit state with fresh data
                    setAudit(prev => prev ? ({
                      ...prev,
                      thread: {
                        ...(prev.thread || {}),
                        message_count: data.message_count,
                      },
                      progress_summary: data.progress_summary,
                    }) : prev);
                  }}
                />
              </div>
            )}

            {/* ── AGENT LOG ── */}
            {activeTab === 'log' && hasAgentData && (
              <div className="p-5 space-y-4">
                <div>
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <Bot className="w-4 h-4 text-primary" />Agent Reasoning & Actions
                  </h3>
                  <p className="text-xs text-muted-foreground">Every step the AI took to process this audit</p>
                </div>
                <ScrollArea className="h-[360px]">
                  <div className="space-y-1.5 pr-2">
                    {(audit?.agent_actions || []).map((action, idx) => {
                      const isReasoning = action.startsWith('[Reasoning]');
                      const isThinking  = action.startsWith('[Thinking]');
                      const m = action.match(/^\[([\w-]+)\]/);
                      const toolName = m?.[1];
                      const text = action.replace(/^\[[\w-]+\]\s*/, '');
                      const COLORS = {
                        'extract_from_email':      'text-blue-700 bg-blue-50 border-blue-200',
                        'cross_reference_sources': 'text-orange-700 bg-orange-50 border-orange-200',
                        'run_audit_rules':         'text-emerald-700 bg-emerald-50 border-emerald-200',
                        'draft_audit_email':       'text-primary bg-accent border-primary/20',
                        'pre-process':             'text-slate-600 bg-slate-50 border-slate-200',
                      };
                      const cls = isReasoning
                        ? 'text-violet-800 bg-violet-50 border-violet-200'
                        : isThinking ? 'text-slate-500 bg-white border-border'
                        : COLORS[toolName] || COLORS['pre-process'];
                      return (
                        <div key={idx}
                          className={`flex items-start gap-2 p-2 rounded-lg border text-xs ${cls}`}>
                          <div className="flex-shrink-0 mt-0.5">
                            {isReasoning ? <Bot className="w-3.5 h-3.5" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            {isReasoning && <span className="font-bold text-[10px] mr-2 uppercase tracking-wide">Claude Reasoning</span>}
                            {toolName && !isThinking && !isReasoning && (
                              <span className="font-mono font-bold text-[10px] mr-2 uppercase tracking-wide opacity-60">{toolName}</span>
                            )}
                            <span className={isReasoning ? 'font-mono leading-relaxed' : ''}>
                              {text.length > 200 ? text.slice(0, 200) + '…' : text}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>

                {audit?.discrepancies?.length > 0 && (
                  <>
                    <Separator />
                    <div>
                      <h3 className="text-sm font-semibold flex items-center gap-2 text-orange-700 mb-3">
                        <GitCompare className="w-4 h-4" />Data Discrepancies
                        <span className="ml-1 px-1.5 py-0.5 text-[10px] bg-orange-100 text-orange-700 rounded-full">
                          {audit.discrepancies.length}
                        </span>
                      </h3>
                      <div className="table-scroll">
                        <table className="audit-table w-full text-xs">
                          <thead>
                            <tr className="border-b border-border bg-secondary/50">
                              {['SKU','Field','Email','Document','Diff%','Used'].map(h => <th key={h} className="text-left px-3 py-2">{h}</th>)}
                            </tr>
                          </thead>
                          <tbody>
                            {audit.discrepancies.map((d, idx) => (
                              <tr key={idx} className="border-b border-border/50">
                                <td className="px-3 py-2 font-mono text-[11px] max-w-[120px] truncate" title={d.model}>{d.model}</td>
                                <td className="px-3 py-2">{d.field}</td>
                                <td className="px-3 py-2 tabular-nums text-orange-700">{d.email_value?.toLocaleString()}</td>
                                <td className="px-3 py-2 tabular-nums text-primary font-medium">{d.document_value?.toLocaleString()}</td>
                                <td className="px-3 py-2 tabular-nums">
                                  <span className={d.pct_diff > 20 ? 'text-red-600 font-semibold' : 'text-orange-600'}>{d.pct_diff}%</span>
                                </td>
                                <td className="px-3 py-2">
                                  <span className="px-1.5 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] font-medium">{d.note}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>

      <ComposeModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        initial={composeInitial}
        onSent={(data) => {
          // Optimistically update audit so the resend banner + counter
          // reflect immediately, even before the next refetch.
          setAudit(prev => {
            if (!prev) return prev;
            const entry = {
              sent_at:          new Date().toISOString(),
              to:               composeInitial.to,
              cc:               composeInitial.cc || '',
              subject:          data?.subject || composeInitial.subject,
              gmail_message_id: data?.gmail_message_id,
              thread_id:        data?.thread_id,
            };
            const history = [...(prev.email_sent_history || []), entry];
            return {
              ...prev,
              action_plan: { ...(prev.action_plan || {}), email_sent: true, status: 'awaiting_response' },
              email_sent:          entry,
              email_sent_count:    history.length,
              email_sent_history:  history,
            };
          });
        }}
      />
    </motion.div>
  );
}

function ResultsSkeleton() {
  return (
    <div className="page-pad max-w-[1200px] space-y-4">
      <Skeleton className="h-8 w-40" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-28" />)}
      </div>
      <Skeleton className="h-96" />
    </div>
  );
}
